import React, { useState } from 'react';
import { Sparkles, RefreshCw, ExternalLink, Bookmark } from "lucide-react";
import PaletteDisplay from './PaletteDisplay';
import CompactExportDropdown from './CompactExportDropdown';
import { generateMoodPalette, getMoodPresets } from '../lib/colorUtils';

const MoodBuilder = ({
    onColorClick,
    visibleFields = [],
    colorBlindnessType = 'none',
    isDark = false,
    addColorToPalette,
    setVisualizerColors,
    savePalette,
    addColorToCustomPalette
}) => {
    const [selectedMood, setSelectedMood] = useState('');
    const [colorCount, setColorCount] = useState(5);
    const [generatedPalette, setGeneratedPalette] = useState([]);
    const moods = getMoodPresets();

    const handleGeneratePalette = () => {
        if (!selectedMood) return;

        const palette = generateMoodPalette(selectedMood, colorCount);
        setGeneratedPalette(palette);
    };

    const handleRegeneratePalette = () => {
        if (!selectedMood) return;
        const palette = generateMoodPalette(selectedMood, colorCount);
        setGeneratedPalette(palette);
    };

    const handleExportToVisualizer = (colors, paletteName, event) => {
        const colorObjects = colors.map((color, index) => ({
            id: `${paletteName.toLowerCase().replace(/\s+/g, '-')}-${index}`,
            value: color
        }));

        setVisualizerColors(colorObjects, event.target);
    };

    const handleSavePalette = () => {
        const selectedPreset = moods.find(m => m.id === selectedMood);
        const paletteName = selectedPreset ? `${selectedPreset.name} Mood` : 'Mood Palette';
        savePalette(paletteName, generatedPalette);
    };

    // Generate CSS for palette
    const generatePaletteCss = (colors, paletteName) => {
        const cssVarName = paletteName.toLowerCase().replace(/\s+/g, '-');
        return `:root {\n${colors.map((color, i) => `  --${cssVarName}-${i + 1}: ${color};`).join('\n')}\n}\n\n/* Usage examples */\n.${cssVarName}-primary { color: var(--${cssVarName}-1); }\n.${cssVarName}-bg { background-color: var(--${cssVarName}-1); }`;
    };

    // Generate SVG for palette
    const generatePaletteSvg = (colors, paletteName) => {
        const width = colors.length * 60;
        const height = 80;

        return `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <!-- ${paletteName} Palette -->
  <rect width="${width}" height="${height}" fill="white" stroke="#e5e7eb" stroke-width="1" rx="8"/>
  ${colors.map((color, i) => `
  <rect x="${i * 60 + 10}" y="10" width="50" height="50" fill="${color}" stroke="#fff" stroke-width="2" rx="4"/>
  <text x="${i * 60 + 35}" y="72" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="#666">${color}</text>`).join('')}
</svg>`;
    };

    const selectedPreset = moods.find(m => m.id === selectedMood);

    return (
        <div className="space-y-6 overflow-visible">
            {/* Mood Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Mood-Based Palette Generator
                        </h4>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                        Generate color palettes based on brand personality and emotional qualities
                    </p>
                </div>

                <div className="p-4">
                    {/* Mood Tags Grid */}
                    <div className="mb-6">
                        <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                            Select Brand Mood
                        </h5>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {moods.map(mood => (
                                <button
                                    key={mood.id}
                                    onClick={() => setSelectedMood(mood.id)}
                                    className={`
                                        p-3 rounded-lg border-2 text-left transition-all duration-200
                                        ${selectedMood === mood.id
                                            ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 bg-white dark:bg-gray-800'
                                        }
                                    `}
                                    title={mood.description}
                                >
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {mood.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                        {mood.description}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Color Count Selector */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                Number of Colors
                            </h5>
                            <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                {colorCount}
                            </span>
                        </div>
                        <div className="flex gap-1">
                            {[3, 4, 5, 6, 7, 8].map(count => (
                                <button
                                    key={count}
                                    onClick={() => setColorCount(count)}
                                    className={`
                                        flex-1 h-10 rounded text-sm font-medium transition-colors
                                        ${colorCount === count
                                            ? 'bg-purple-600 dark:bg-purple-500 text-white'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }
                                    `}
                                >
                                    {count}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Ideal range: 3-6 colors for brand palettes
                        </p>
                    </div>

                    {/* Generate Button */}
                    <button
                        onClick={handleGeneratePalette}
                        disabled={!selectedMood}
                        className={`
                            w-full py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors duration-200
                            ${selectedMood
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/40'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            }
                        `}
                    >
                        <Sparkles className="w-4 h-4" />
                        {generatedPalette.length > 0 ? 'Regenerate Palette' : 'Generate Palette'}
                    </button>

                    {selectedPreset && (
                        <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                            <div className="flex items-start gap-2">
                                <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-xs font-medium text-purple-900 dark:text-purple-100">
                                        {selectedPreset.name}
                                    </p>
                                    <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                                        {selectedPreset.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Generated Palette Display */}
            {generatedPalette.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    Generated Palette
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {generatedPalette.length} colors • {selectedPreset?.name} mood
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleRegeneratePalette}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors duration-200"
                                    title="Generate a new variation"
                                >
                                    <RefreshCw className="w-3 h-3" />
                                    New
                                </button>
                                <button
                                    onClick={handleSavePalette}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
                                >
                                    <Bookmark className="w-3 h-3" />
                                    Save
                                </button>
                                <button
                                    onClick={(event) => handleExportToVisualizer(generatedPalette, `${selectedPreset?.name || 'Mood'} Palette`, event)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                                >
                                    <ExternalLink className="w-3 h-3" />
                                    Visualizer
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="p-4">
                        <PaletteDisplay
                            colors={generatedPalette}
                            onColorClick={onColorClick}
                            visibleFields={visibleFields}
                            colorBlindnessType={colorBlindnessType}
                            isDark={isDark}
                            addColorToPalette={addColorToPalette}
                            addColorToCustomPalette={addColorToCustomPalette}
                        />
                    </div>
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                        <CompactExportDropdown
                            colors={generatedPalette}
                            paletteName={`${selectedPreset?.name || 'Mood'} Palette`}
                            getCss={() => generatePaletteCss(generatedPalette, `${selectedPreset?.name || 'Mood'} Palette`)}
                            getSvg={() => generatePaletteSvg(generatedPalette, `${selectedPreset?.name || 'Mood'} Palette`)}
                        />
                    </div>
                </div>
            )}

            {/* Tips */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Mood Builder Tips
                </h4>
                <ul className="text-xs text-purple-800 dark:text-purple-200 space-y-1">
                    <li>• Select a mood that matches your brand's personality</li>
                    <li>• Generate multiple variations to find the perfect palette</li>
                    <li>• Each generation creates unique colors within the mood's range</li>
                    <li>• Combine with manual builder for fine-tuning</li>
                    <li>• Test palettes in the Visualizer to see them in action</li>
                </ul>
            </div>
        </div>
    );
};

export default MoodBuilder;
